import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, HeartHandshake } from "lucide-react";

export default function Onboarding() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [accountType, setAccountType] = useState("student");
  const [mentorId, setMentorId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet("/organizations").then(setOrgs).catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    if (orgId && accountType === "student") {
      apiGet(`/organizations/${orgId}/mentors`).then(setMentors).catch(() => setMentors([]));
      setMentorId("");
    }
  }, [orgId, accountType]);

  const submit = async () => {
    if (!orgId) return toast.error("Please choose your organization");
    if (accountType === "student" && !mentorId) return toast.error("Please choose a mentor");
    setBusy(true);
    try {
      await apiPost("/onboarding/complete", {
        organization_id: orgId,
        account_type: accountType,
        mentor_id: accountType === "student" ? mentorId : null,
      });
      await logout();
      toast.success(
        "Request sent! Your organization leader will approve it, then you can sign in. We'll email you when you're approved.",
        { duration: 10000 }
      );
      navigate("/login");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <div className="mx-auto max-w-xl px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <Logo type="wordmark" variant="black" height={30} />
          <button onClick={logout} className="text-sm font-semibold underline text-muted-foreground" data-testid="onboarding-signout">
            Sign out
          </button>
        </div>

        <div className="bg-white border-2 border-ink rounded-3xl shadow-pop p-8 animate-rise" data-testid="onboarding-card">
          <h1 className="text-3xl font-extrabold mb-1">Welcome, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-muted-foreground mb-7">Let's connect you to your organization.</p>

          <label className="block text-sm font-bold mb-2">Your organization</label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="border-2 border-ink h-12 rounded-xl mb-2" data-testid="onboarding-org-select">
              <SelectValue placeholder="Select your organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.id}`}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mb-6">
            Can't find yours?{" "}
            <a href="/apply-leader" className="underline font-semibold text-foreground" data-testid="onboarding-apply-leader">
              Apply to list your organization
            </a>
            .
          </p>

          <label className="block text-sm font-bold mb-2">I am a…</label>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { key: "student", label: "Student", icon: GraduationCap, color: "#FF5722" },
              { key: "mentor", label: "Mentor", icon: HeartHandshake, color: "#31B8FF" },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = accountType === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setAccountType(opt.key)}
                  data-testid={`account-type-${opt.key}`}
                  className={`press rounded-2xl border-2 border-ink p-4 text-left ${active ? "shadow-pop" : "bg-white"}`}
                  style={active ? { background: opt.color, color: opt.key === "student" ? "#FFFDF5" : "#0F1116" } : {}}
                >
                  <Icon size={24} className="mb-2" />
                  <div className="font-bold">{opt.label}</div>
                </button>
              );
            })}
          </div>

          {accountType === "student" && (
            <div className="mb-6 animate-rise">
              <label className="block text-sm font-bold mb-2">Choose your mentor</label>
              <Select value={mentorId} onValueChange={setMentorId} disabled={!orgId}>
                <SelectTrigger className="border-2 border-ink h-12 rounded-xl" data-testid="onboarding-mentor-select">
                  <SelectValue placeholder={orgId ? "Select an approved mentor" : "Pick an organization first"} />
                </SelectTrigger>
                <SelectContent>
                  {mentors.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No approved mentors yet</div>
                  )}
                  {mentors.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`mentor-option-${m.id}`}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {accountType === "mentor" && (
            <div className="mb-6 p-4 rounded-2xl border-2 border-ink" style={{ background: "#FFFDF5" }}>
              <p className="text-sm">
                You'll be added as a <b>mentor applicant</b> for this organization. An
                admin or organization leader approves mentors before students can pick
                them. You can study classes while you wait.
              </p>
            </div>
          )}

          <Button onClick={submit} disabled={busy} data-testid="onboarding-submit"
            className="w-full h-12 rounded-full border-2 border-ink shadow-pop press font-bold">
            {busy ? "Saving…" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
